import React, { useState, useMemo } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  Label,
  Badge,
} from "reactstrap";
import { Link } from "react-router-dom";
import BreadCrumb from "Components/Common/BreadCrumb";
import { useGetProjectsByDropDownQuery } from "slices/projects/projectSlice";
import TableContainer from "Components/Common/TableContainer";
import Loader from "Components/Common/Loader";
import { Tooltip } from "react-tooltip";
import { formatDateTime } from "helpers/date_utils";
import { IsValidPermissions } from "../../../Routes/AuthProtected";

import {
  useGetPurchaseRequestsQuery,
  // useCreatePurchaseOrderMutation, // Commented out as per requirements
} from "slices/InventoryManagement/inventoryManagementSlice";

const PurchaseOrdersListView = () => {
  const [page, setPageState] = useState<{
    page: number;
    pageSize: number;
    search: string;
    status: string;
    project: string;
  }>({
    page: 1,
    pageSize: 10,
    search: "",
    status: "",
    project: "",
  });

  const {
    data: purchaseOrders,
    isLoading,
    isFetching,
  } = useGetPurchaseRequestsQuery(page);

  // Commented out create functionality as per requirements
  // const [createPurchaseOrder, { isLoading: isCreating }] = useCreatePurchaseOrderMutation();

  const { data: projectData } = useGetProjectsByDropDownQuery(undefined);

  // Commented out modal and create states as per requirements
  // const [isModalOpen, setIsModalOpen] = useState(false);
  // const [newOrderData, setNewOrderData] = useState({
  //   title: "",
  //   project: "",
  // });

  // Define status options for badge display
  const getStatusBadge = (status: string) => {
    const statusConfig: { [key: string]: { color: string; label: string } } ={
      draft: { color: "secondary", label: "DRAFT" },
      submitted: { color: "warning", label: "SUBMITTED" },
      approved: { color: "success", label: "APPROVED" },
      rejected: { color: "danger", label: "REJECTED" },
      resubmit: { color: "info", label: "RESUBMIT" },
      ordered: { color: "info", label: "ORDERED" },
      delivered: { color: "primary", label: "DELIVERED" },
      cancelled: { color: "danger", label: "CANCELLED" },
    };

    const config = statusConfig[status] || {
      color: "secondary",
      label: status,
    };
    return <Badge color={config.color}>{config.label}</Badge>;
  };

  // Map Projects to Dropdown Options
  const projectOptions = useMemo(() => {
    return (
      projectData?.map((project: any) => ({
        value: project.id,
        label: `${project.project_name} (${project.project_short_name})`,
      })) || []
    );
  }, [projectData]);

  // Define Columns for Table
  const columns = useMemo(
    () => [
      {
        Header: "Order Fulfilment No",
        accessor: "purchase_order_number",
        Cell: ({
          row: {
            original: { id, purchase_order_number },
          },
        }: any) => (
          <Link
            to={`/purchase-orderdetail-list/${id}/`}
            className="text-primary d-inline-block edit-item-btn view-task-status-class"
          >
            {purchase_order_number || id}
          </Link>
        ),
      },
      {
              Header: "Purchase Request No",
              accessor: "purchase_request_number",
              Cell: ({
                row: {
                  original: { id, purchase_request_number },
                },
              }: any) => (
                <Link
                  to={`/purchase-orderdetail-list/${id}/`}
                  className="text-primary d-inline-block edit-item-btn view-task-status-class"
                >
                  {purchase_request_number}
                </Link>
              ),
            },
      {
        Header: "Title",
        accessor: "title",
        Cell: (cell: { value: any }) => (
          <span className="fw-semibold">{cell.value || "N/A"}</span>
        ),
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: (cell: { value: any }) => getStatusBadge(cell.value),
      },

      {
        Header: "Total Items",
        accessor: "items_count",
        Cell: (cell: { value: any }) => (
          <Badge color="info" pill>
            {cell.value || 0}
          </Badge>
        ),
      },
      {
        Header: "Total PO'S",
        accessor: "po_count",
        Cell: (cell: { value: any }) => (
          <Badge color="info" pill>{cell.value || 0}</Badge>
        ),
      },
      {
        Header: "Project",
        accessor: "project_name",
      },
      {
        Header: "Submitted By",
        accessor: "submitted_by_name",
      },
      // {
      //   Header: "Created Date",
      //   accessor: "request_created_date",
      //   Cell: ({ value }: any) =>
      //     value ? formatDateTime(value, "datetime") : "N/A",
      // },
      {
        Header: "Submitted Date",
        accessor: "request_submitted_date",
        Cell: ({ value }: any) =>
          value ? formatDateTime(value, "datetime") : "-",
      },
      {
        Header: "Actions",
        accessor: "actions",
        Cell: ({
          row: {
            original: { id, status },
          },
        }: any) => (
          <div className="d-flex gap-2">
            <ul className="list-inline hstack gap-2 mb-0">
              {(IsValidPermissions(["VIEW_ONLY_PURCHASE_ORDERFULLFILMENT"]) ||
                IsValidPermissions(["VIEW_ALL_PURCHASE_ORDERFULLFILMENT"])) && (
                  <li className="list-inline-item edit" title="View">
                    <Link
                      to={`/purchase-orderfulfilment/${id}/`}
                      className="text-primary d-inline-block edit-item-btn view-task-status-class"
                    >
                      <i className="ri-eye-fill fs-16"></i>
                    </Link>
                  </li>
                )}
              {IsValidPermissions(["EDIT_PURCHASE_ORDERFULLFILMENT"]) &&
                (status === "submitted" || status === "rejected" || status === "approved" || status === "resubmit") ? (
                <li className="list-inline-item edit" title="Edit">
                  <Link
                    to={`/purchase-orderfulfilment/${id}`}
                    className="text-primary d-inline-block edit-item-btn edit-task-status-class"
                  >
                    <i className="ri-pencil-fill fs-16"></i>
                  </Link>
                </li>
              ) : (
                <>
                  <i
                    className="ri-pencil-fill fs-16 edit-task-status-class"
                    title="Edit"
                    style={{ color: "gray" }}
                  ></i>
                </>
              )}

            </ul>
          </div>
        ),
      },
    ],
    []
  );

  return (
    <React.Fragment>
      <Tooltip
        style={{ zIndex: 10 }}
        anchorSelect=".view-task-status-class"
        place="bottom"
        content="View"
      />
      <Tooltip
        style={{ zIndex: 10 }}
        anchorSelect=".edit-task-status-class"
        place="bottom"
        content="Edit"
      />
      <Tooltip
        style={{ zIndex: 10 }}
        anchorSelect=".inventory-task-status-class"
        place="bottom"
        content="Manage Inventory"
      />
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Purchase Orders Fulfilment" pageTitle="Inventory" />
          <Row>
            <Col lg={12}>
              <Card id="purchaseOrdersList">
                <CardHeader className="border-0">
                  <Row className="g-4 align-items-center">
                    <Col sm>
                      <div>
                        <h5 className="card-title mb-0">
                          Purchase Orders Fulfilment
                        </h5>
                      </div>
                    </Col>
                    <Col sm="auto">
                      <div>
                        {/* Add button commented out as per requirements */}
                        {/* {IsValidPermissions(["ADD_PURCHASE_ORDER"]) && ( 
                          <Button color="success" onClick={toggleModal}>
                            <i className="ri-add-line align-bottom me-1"></i>
                            New Purchase Order
                          </Button>
                        )} */}
                      </div>
                    </Col>
                  </Row>
                </CardHeader>
                <div className="card-body pt-0">
                  {isLoading ? (
                    <Loader />
                  ) : (
                    <TableContainer
                      columns={columns}
                      data={purchaseOrders?.results || []}
                      isLoading={isFetching}
                      isGlobalFilter={true}
                      isAddUserList={false}
                      customPageSize={page.pageSize}
                      className="custom-header-css"
                      divClass="table-responsive table-card mb-3"
                      tableClass="align-middle table-nowrap mb-0"
                      theadClass="table-light text-muted"
                      thClass="table-light text-muted"
                      isPurchaseRequestFilter={true}
                      SearchPlaceholder="Search for Purchase Orders..."
                      isPagination={false}
                      isServerPagination={true}
                      serverPaginationProps={{
                        count: purchaseOrders?.count || 0,
                        next: purchaseOrders?.next || null,
                        page_size: purchaseOrders?.page_size || 10,
                        previous: purchaseOrders?.previous || null,
                        current_page: purchaseOrders?.current_page || 1,
                        total_page: purchaseOrders?.total_page || 0,
                      }}
                      setPageState={setPageState}
                    />
                  )}
                </div>
              </Card>
            </Col>
          </Row>
        </Container>

        {/* Modal commented out as per requirements */}
        {/* <Modal isOpen={isModalOpen} toggle={toggleModal}>
          <ModalHeader toggle={toggleModal}>
            Create New Purchase Order
          </ModalHeader>
          <ModalBody>
            <div className="mb-3">
              <Label for="orderTitle">Title <span className="text-danger">*</span></Label>
              <Input
                type="text"
                id="orderTitle"
                name="title"
                placeholder="Enter purchase order title"
                value={newOrderData.title}
                onChange={handleInputChange}
              />
            </div>
            <div className="mb-3">
              <Label for="projectSelect">Project <span className="text-danger">*</span></Label>
              <Input
                type="select"
                id="projectSelect"
                name="project"
                value={newOrderData.project}
                onChange={handleInputChange}
              >
                <option value="">Select Project</option>
                {projectOptions.map((project: any) => (
                  <option key={project.value} value={project.value}>
                    {project.label}
                  </option>
                ))}
              </Input>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              color="success"
              onClick={handleCreateNewOrder}
              disabled={!newOrderData.title.trim() || !newOrderData.project || isCreating}
            >
              {isCreating ? "Creating..." : "Create Purchase Order"}
            </Button>
            <Button color="danger" onClick={toggleModal}>
              Cancel
            </Button>
          </ModalFooter>
        </Modal> */}
      </div>
    </React.Fragment>
  );
};

export default PurchaseOrdersListView;
